import { captureException } from '@canvas/sentry-react';

export enum CanvasLocalStorageKey {
  SHOW_TEMPLATE_MODAL_PREFERENCE = 'show-template-modal-preference',
  AWC_SESSION_ID = 'awc.session.id',
  WHITEBOARD_USER_PREFERENCES_BACKGROUND = 'whiteboard.userPreferences.background',
  PRIVATE_MODE_ANONYMOUS_ID = 'whiteboard.privateMode.anonymous.userid',
  GPU_DATA = 'gpu-data-v3',
  IDENTITY_V3 = 'identity_v3',
  JIRA_PREFERENCE_SITE_ID = 'jira-preference-site-id',
  JIRA_PREFERENCE_PROJECT_ID = 'jira-preference-project-id',
  JIRA_PREFERENCE_COLUMN_VISIBILITY = 'preference-column-visibility',
  SLIDES_INGESTION_STATE = 'slides-ingestion-state',
}

/**
 * Safely gets a string from localstorage.
 * If an error occurs then undefined is returned and the error is sent to Sentry.
 *
 * @param key the CanvasLocalStorageKey of the item to retrieve
 * @returns string, null or undefined
 */
export const safeGet = (key: CanvasLocalStorageKey): string | null | undefined => {
  try {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(key);
    }
  } catch (e) {
    captureException(e, {
      tags: {
        captureLocation: 'CanvasLocalStorage',
      },
    });
  }
};

/**
 * Safely sets a string in localstorage.
 * If an error occurs then undefined is returned and the error is sent to Sentry.
 *
 * @param key the CanvasLocalStorageKey of the item to set
 * @param value the string to set
 * @returns nothing
 */
export const safeSet = (key: CanvasLocalStorageKey, value: string): void => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, value);
    }
  } catch (e) {
    captureException(e, {
      tags: {
        captureLocation: 'CanvasLocalStorage',
      },
    });
  }
};

/**
 * Safely gets an object from localstorage.
 * If an error occurs then undefined is returned and the error is sent to Sentry.
 *
 * Internally this uses JSON.parse to deserialilse the object.
 *
 * NOTE: the object returned is not guaranteed to match the type T!
 *
 * @param key the CanvasLocalStorageKey of the item to retrieve
 * @returns string, null or undefined
 */
export const safeGetObject = <T>(key: CanvasLocalStorageKey): T | undefined => {
  const item = safeGet(key);
  if (item == null) {
    return undefined;
  }

  try {
    return JSON.parse(item) as T;
  } catch (e) {
    captureException(e, {
      tags: {
        captureLocation: 'CanvasLocalStorage.safeGetObject',
      },
    });

    return undefined;
  }
};

/**
 * Safely sets an object in localstorage. The object can contain anything JSON serialisable, including arrays.
 * If an error occurs then undefined is returned and the error is sent to Sentry.
 *
 * Internally this converts the object to a JSON string.
 *
 * @param key the CanvasLocalStorageKey of the item to set
 * @param value the object to set
 * @returns nothing
 */
export const safeSetObject = <T>(key: CanvasLocalStorageKey, value: T): void => {
  return safeSet(key, JSON.stringify(value));
};

/**
 * Safely removes an item from localstorage.
 * If an error occurs it is sent to Sentry.
 *
 * @param key the CanvasLocalStorageKey of the item to remove
 * @returns nothing
 */
export const safeRemove = (key: CanvasLocalStorageKey): void => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(key);
    }
  } catch (e) {
    captureException(e, {
      tags: {
        captureLocation: 'CanvasLocalStorage',
      },
    });
  }
};
